import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { Empty } from "antd";
import { FileImageOutlined, FileTextOutlined } from "@ant-design/icons";

import { GalleryDraftBar } from "../component/gallery";
import { GalleryOrgan, useGalleryOrgans } from "../hooks/useGalleryOrgans";
import { ACCESS_TOKEN } from "../lib/cookie";
import { MainColor, SubColor } from "../style/styleValue";

const COLLAGE_MAX = 3; // 블록당 보여줄 썸네일 수 (1 크게 + 2 작게)

const GalleryDraftMosaic = () => {
  const navigate = useNavigate();
  const { organs, loading, error } = useGalleryOrgans();

  useEffect(() => {
    if (!localStorage.getItem(ACCESS_TOKEN)) navigate("/login");
  }, [navigate]);

  const totalPosts = organs.reduce((sum, o) => sum + o.postCnt, 0);
  const totalSlides = organs.reduce((sum, o) => sum + o.slideCnt, 0);

  return (
    <Page>
      <GalleryDraftBar active="mosaic" />

      <PageHeader>
        <Eyebrow>ATLAS GALLERY</Eyebrow>
        <Title>Digital Slide Archive</Title>
        <Count>
          {totalPosts.toLocaleString()} posts · {totalSlides.toLocaleString()} slides
        </Count>
      </PageHeader>

      {error ? (
        <CenterState>
          <Empty description="갤러리 목록을 불러오지 못했습니다." />
        </CenterState>
      ) : loading ? (
        <Wall>
          {Array.from({ length: 9 }).map((_, i) => (
            <SkeletonBlock key={i} />
          ))}
        </Wall>
      ) : organs.length === 0 ? (
        <CenterState>
          <Empty description="표시할 organ 이 없습니다." />
        </CenterState>
      ) : (
        <Wall>
          {organs.map((organ) => (
            <MosaicBlock key={organ.id} organ={organ} onClick={() => navigate(`/post-list/${organ.id}`)} />
          ))}
        </Wall>
      )}
    </Page>
  );
};

const MosaicBlock = ({ organ, onClick }: { organ: GalleryOrgan; onClick: () => void }) => {
  const shown = organ.thumbs.slice(0, COLLAGE_MAX);
  const rest = Math.max(organ.slideCnt - shown.length, 0);

  return (
    <Block onClick={onClick} title={organ.name}>
      <Collage $count={shown.length}>
        {shown.length === 0 ? (
          organ.thumbsLoading ? (
            <Shimmer />
          ) : (
            <NoImage>{organ.name.slice(0, 2).toUpperCase()}</NoImage>
          )
        ) : (
          shown.map((url, i) => (
            <Tile key={url}>
              <img
                src={url}
                alt={`${organ.name} ${i + 1}`}
                loading="lazy"
                onError={(e) => {
                  const el = e.currentTarget;
                  el.style.display = "none";
                  (el.parentElement as HTMLElement).classList.add("broken");
                }}
              />
              {i === shown.length - 1 && rest > 0 && <More>+{rest}</More>}
            </Tile>
          ))
        )}
      </Collage>

      <Meta>
        <MTitle>{organ.name}</MTitle>
        <MCounts>
          <span>
            <FileTextOutlined /> {organ.postCnt}
          </span>
          <span>
            <FileImageOutlined /> {organ.slideCnt}
          </span>
        </MCounts>
      </Meta>
      {organ.subNames.length > 0 && (
        <SubChips>
          {organ.subNames.slice(0, 3).map((name) => (
            <Chip key={name}>{name}</Chip>
          ))}
          {organ.subNames.length > 3 && <Chip $muted>+{organ.subNames.length - 3}</Chip>}
        </SubChips>
      )}
    </Block>
  );
};

const shimmer = keyframes`
  0% { background-position: -320px 0; }
  100% { background-position: 320px 0; }
`;

const Page = styled.div`
  width: 100%;
  padding-bottom: 5rem;
  background: #fff;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 3rem 2rem 2rem;
  margin-bottom: 2.5rem;
  border-bottom: 1px solid #eef1f5;
`;

const Eyebrow = styled.span`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  color: ${SubColor};
  margin-bottom: 10px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 2.4rem;
  font-weight: 700;
  color: ${MainColor};
  letter-spacing: -0.5px;
`;

const Count = styled.span`
  margin-top: 10px;
  font-size: 0.95rem;
  color: #999;
`;

const CenterState = styled.div`
  min-height: 40vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Wall = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 18px;
  padding: 0 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 0 1.25rem;
  }
`;

const Collage = styled.div<{ $count: number }>`
  display: grid;
  gap: 4px;
  aspect-ratio: 16 / 10;
  overflow: hidden;
  border-radius: 10px;
  background: #f0f2f5;

  /* 썸네일 수에 맞춰 빈칸 없이 채워지는 배치 (1장: 전체 / 2장: 좌우 / 3장 이상: 좌측 큰 1 + 우측 작은 2) */
  ${({ $count }) =>
    $count >= 3
      ? `grid-template-columns: 2fr 1fr;
         grid-template-rows: 1fr 1fr;
         & > *:first-child { grid-row: span 2; }`
      : $count === 2
      ? `grid-template-columns: 1fr 1fr;`
      : `grid-template-columns: 1fr;`}
`;

const Block = styled.article`
  padding: 12px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.05), 0 0 0 1px #eef1f5;
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    box-shadow: 0 12px 26px rgba(0, 0, 0, 0.1), 0 0 0 1px #dbe2ea;
    transform: translateY(-3px);
  }
`;

const Tile = styled.div`
  position: relative;
  overflow: hidden;
  background: #f0f2f5;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.4s ease;
  }

  ${Block}:hover & img {
    transform: scale(1.05);
  }

  &.broken::after {
    content: "";
    position: absolute;
    inset: 0;
    background: repeating-linear-gradient(45deg, #f0f2f5, #f0f2f5 10px, #e8ebf0 10px, #e8ebf0 20px);
  }
`;

const More = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(10, 24, 42, 0.5);
  color: #fff;
  font-size: 1rem;
  font-weight: 700;
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 12px;
`;

const MTitle = styled.h3`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #222;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${Block}:hover & {
    color: ${MainColor};
  }
`;

const MCounts = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  color: #667085;
  font-size: 0.75rem;

  span {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
  .anticon {
    font-size: 11px;
  }
`;

const SubChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 10px;
`;

const Chip = styled.span<{ $muted?: boolean }>`
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.68rem;
  color: ${({ $muted }) => ($muted ? "#a8b1bd" : "#5c6b7f")};
  background: #f2f4f7;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Shimmer = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #eef1f5 25%, #f7f9fb 50%, #eef1f5 75%);
  background-size: 640px 100%;
  animation: ${shimmer} 1.3s linear infinite;
`;

const NoImage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  font-weight: 700;
  color: #c6d0dc;
  background: linear-gradient(135deg, #f5f7fa 0%, #e9eef4 100%);
`;

const SkeletonBlock = styled.div`
  aspect-ratio: 16 / 12;
  border-radius: 14px;
  background: linear-gradient(90deg, #eef1f5 25%, #f7f9fb 50%, #eef1f5 75%);
  background-size: 640px 100%;
  animation: ${shimmer} 1.3s linear infinite;
`;

export default GalleryDraftMosaic;
